// SocialHub.js
// Social Media Suite hub. Lists closed shifts; opening one shows a detail view
// where the operator:
//   • previews the recap Instagram Story — STATIC (server-rendered) and a live
//     ANIMATED hero-style drift (all the shift's pancakes rising with sparkles),
//   • stars which pancakes "spotlight" (used for the static post collage AND,
//     on Save, elevated to public-gallery showpieces via order.isShowcase).
// Starring is staged locally; nothing writes to the DB until "Save spotlights".

import { useState, useEffect, useRef } from "react";
import { db } from "./firebase";
import { collection, query, where, orderBy, limit, onSnapshot, doc, getDocs, updateDoc } from "firebase/firestore";
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import { STAFF_THEME as T, FONT } from "./theme/staff";
import StoryAnimator from "./social/StoryAnimator";
import { computeOverlay } from "./social/storyOverlay";

export default function SocialHub({ navigate }) {
  const [shifts, setShifts] = useState([]);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    const q = query(collection(db, "shift_summaries"), orderBy("closedAt", "desc"), limit(40));
    return onSnapshot(q, (snap) => setShifts(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
  }, []);

  const fmt = (ts) => {
    const ms = ts?.toMillis?.() ?? null;
    if (ms == null) return "";
    return new Date(ms).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "America/Chicago" });
  };

  return (
    <div style={{ minHeight: "100vh", background: T.bg, fontFamily: FONT.body, color: T.text }}>
      <div style={{ padding: "0.75rem 1rem", background: T.panel, borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: "1rem", position: "sticky", top: 0, zIndex: 5 }}>
        <div style={{ color: T.gold, fontFamily: FONT.display, fontWeight: 700, fontSize: "1.05rem", flex: 1 }}>📣 Social Studio</div>
        {openId
          ? <button onClick={() => setOpenId(null)} style={navBtn}>← Shifts</button>
          : <button onClick={() => navigate("/menu")} style={navBtn}>← Menu</button>}
      </div>

      {openId
        ? <ShiftStudio key={openId} shiftId={openId} />
        : (
          <div style={{ maxWidth: 560, margin: "0 auto", padding: "1.25rem 1rem 2.5rem" }}>
            <div style={{ color: T.muted, fontSize: "0.85rem", lineHeight: 1.55, marginBottom: "1.25rem" }}>
              Generate shareable assets from your shifts. Open a shift to preview its{" "}
              <strong style={{ color: T.cream }}>Instagram Story</strong> — static and animated — and spotlight your favorite pancakes.
              <br /><span style={{ color: T.dim, fontSize: "0.78rem" }}>Weekly carousel + testimonial / meditation cards coming soon.</span>
            </div>
            <div style={sectionLabel}>By Shift</div>
            {shifts.length === 0 && <div style={{ color: T.dim, fontSize: "0.85rem", padding: "1rem 0.25rem" }}>No closed shifts yet.</div>}
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              {shifts.map((s) => (
                <button key={s.id} onClick={() => setOpenId(s.id)} style={rowBtn}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: T.text, fontWeight: 700, fontSize: "0.92rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.eventName || "Unnamed shift"}</div>
                    <div style={{ color: T.dim, fontSize: "0.76rem", marginTop: "0.15rem" }}>{fmt(s.openedAt ?? s.closedAt)}{s.totalOrders != null ? ` · ${s.totalOrders} pancake${s.totalOrders === 1 ? "" : "s"}` : ""}</div>
                  </div>
                  <span style={{ color: T.gold, fontSize: "0.85rem", fontWeight: 700, whiteSpace: "nowrap" }}>Open →</span>
                </button>
              ))}
            </div>
          </div>
        )}
    </div>
  );
}

// ── Per-shift studio ─────────────────────────────────────────────────────────
function ShiftStudio({ shiftId }) {
  const [recap, setRecap] = useState(undefined);  // undefined=loading, null=missing
  const [savedSet, setSavedSet] = useState(null); // persisted isShowcase orderIds
  const [selected, setSelected] = useState(null); // staged spotlight orderIds
  const [tab, setTab] = useState("animated");
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState(null);
  const [primaryId, setPrimaryId] = useState(null); // explicit hero (big spot); separate from starring
  const [vidBusy, setVidBusy] = useState(false);
  const [vidKicked, setVidKicked] = useState(false); // tap registered, waiting for the server to claim
  const vidBlobRef = useRef(null);  // fetched mp4 kept for instant share on a retry tap
  const recapRef = useRef(null);    // latest recap for use inside async catch blocks
  const [, setClockTick] = useState(0); // re-render pulse so the elapsed label moves

  // Live recap subscription — the mp4 render runs server-side long after the
  // CDN drops the kick-off request, so progress (videoRenderStatus / videoAssetUrl
  // / videoRenderProgress) arrives only through this listener.
  useEffect(() => {
    return onSnapshot(doc(db, "publicShiftRecaps", shiftId),
      (s) => { const d = s.exists() ? s.data() : null; recapRef.current = d; setRecap(d); },
      () => setRecap(null));
  }, [shiftId]);

  // Authoritative current spotlight state = the shift's orders' isShowcase.
  // One-shot per shift (savedSet guard) — recap snapshots from render-status
  // writes must not reset a selection in progress.
  useEffect(() => {
    if (!recap || savedSet !== null) return;
    let alive = true;
    (async () => {
      try {
        const os = await getDocs(query(collection(db, "orders"), where("shiftId", "==", shiftId)));
        if (!alive) return;
        const showcased = new Set();
        os.forEach((d) => { if (d.data().isShowcase) showcased.add(d.id); });
        setSavedSet(showcased);
        setSelected(new Set(showcased));
      } catch { /* selection starts empty; starring still works */ }
    })();
    return () => { alive = false; };
  }, [recap, savedSet, shiftId]);

  // ── Video render state (must precede the early returns: hooks below) ───────
  // READINESS COMES FROM THE DOC, never a baked-in version constant — a tab
  // left open across a deploy must still flip when the server says ready. If
  // the server's build version moved on, the fetch itself triggers the
  // re-render and the doc flips back to "rendering"; the UI just follows.
  const videoUrl = `${window.location.origin}/social-asset/shift-video/${shiftId}.mp4`;
  const videoReady = recap?.videoRenderStatus === "ready" && !!recap?.videoAssetUrl;
  const renderClaimStale = (recap?.videoRenderStartedAtMs || 0) < Date.now() - 10 * 60 * 1000;
  const videoRendering = recap?.videoRenderStatus === "rendering" && !renderClaimStale;
  const videoError = recap?.videoRenderStatus === "error";
  const videoPct = videoRendering ? Math.round(Math.min(0.97, recap?.videoRenderProgress ?? 0) * 100) : 0;
  const videoElapsedS = videoRendering ? Math.max(0, Math.round((Date.now() - recap.videoRenderStartedAtMs) / 1000)) : 0;

  // Tick a re-render every second while rendering so the elapsed label visibly
  // moves even between server progress writes — it must never look frozen.
  useEffect(() => {
    if (!videoRendering) return;
    const t = setInterval(() => setClockTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [videoRendering]);

  // "Starting…" covers the gap between the tap and the server's claim landing.
  useEffect(() => {
    if (vidKicked && (videoRendering || videoReady || videoError)) setVidKicked(false);
  }, [vidKicked, videoRendering, videoReady, videoError]);
  useEffect(() => {
    if (!vidKicked) return;
    const t = setTimeout(() => {
      setVidKicked(false);
      if (!recapRef.current?.videoRenderStatus) setNote("✗ The render didn’t start — check connection and tap again.");
    }, 60000);
    return () => clearTimeout(t);
  }, [vidKicked]);

  if (recap === undefined) return <div style={{ padding: "2rem", color: T.dim, textAlign: "center" }}>Loading…</div>;
  if (recap === null) return <div style={{ padding: "2rem", color: T.dim, textAlign: "center", lineHeight: 1.6 }}>No recap built for this shift yet.<br />Open it in <strong style={{ color: T.cream }}>/library → Shifts</strong> and tap “Rebuild recap”.</div>;

  const pancakes = (recap.pancakes || []).filter((p) => p.cutoutUrl || p.thumbUrl);
  const sel = selected || new Set();
  const saved = savedSet || new Set();
  const overlay = computeOverlay(recap);
  const idsParam = encodeURIComponent([...sel].join(","));
  // hero = explicit primary if it's still starred, else the longest-draw of the selection
  const selList = pancakes.filter((p) => sel.has(p.orderId));
  const longestSelId = selList.length ? selList.reduce((a, b) => ((b.drawTimeMs ?? -1) > (a.drawTimeMs ?? -1) ? b : a)).orderId : null;
  const heroId = (primaryId && sel.has(primaryId)) ? primaryId : longestSelId;
  const primParam = (primaryId && sel.has(primaryId)) ? encodeURIComponent(primaryId) : "";
  const staticUrl = `${window.location.origin}/social-asset/shift-story/${shiftId}.jpg?ids=${idsParam}&primary=${primParam}`;
  const dirty = sel.size !== saved.size || [...sel].some((id) => !saved.has(id));

  function toggle(orderId) {
    const had = sel.has(orderId);
    setSelected((prev) => { const n = new Set(prev); had ? n.delete(orderId) : n.add(orderId); return n; });
    if (had && orderId === primaryId) setPrimaryId(null); // unstarring the hero clears it
  }
  function setPrimary(orderId) {
    setSelected((prev) => { const n = new Set(prev); n.add(orderId); return n; }); // hero must be included
    setPrimaryId(orderId);
  }

  // Share with a download safety net. iOS Safari invalidates the tap's "user
  // activation" after a few seconds of async work — a slow asset fetch makes
  // navigator.share throw NotAllowedError even though the bytes arrived fine.
  // In that case (or any non-cancel share failure) we save the file instead,
  // and a retry tap can share instantly because the blob is kept in a ref.
  function saveBlob(blob, filename) {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 3000);
  }
  async function shareOrSave(blob, filename, mime) {
    const file = new File([blob], filename, { type: mime });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: "Shift recap", text: recap.eventName || "The Joy of Pancakes" });
        return "shared";
      } catch (e) {
        if (e.name === "AbortError") return "cancelled"; // user closed the sheet
        // gesture expired (NotAllowedError) or share otherwise refused → save
      }
    }
    saveBlob(blob, filename);
    return "saved";
  }

  async function shareStatic() {
    setBusy(true); setNote(null);
    try {
      const res = await fetch(staticUrl);
      if (!res.ok) throw new Error("not ready");
      const blob = await res.blob();
      const how = await shareOrSave(blob, `recap-${shiftId}.jpg`, "image/jpeg");
      if (how === "saved") setNote("⬇ Saved the image — tap again for the share sheet.");
    } catch (e) { setNote("✗ Couldn’t prepare image — try Rebuild recap."); }
    setBusy(false);
  }

  async function saveSpotlights() {
    setSaving(true); setNote(null);
    try {
      const changed = [];
      pancakes.forEach((p) => {
        const want = sel.has(p.orderId), had = saved.has(p.orderId);
        if (want !== had) changed.push({ id: p.orderId, want });
      });
      await Promise.all(changed.map((c) => updateDoc(doc(db, "orders", c.id), {
        isShowcase: c.want, showcasedAt: c.want ? new Date() : null,
      })));
      setSavedSet(new Set(sel));
      setNote(`✓ Saved ${sel.size} spotlight${sel.size === 1 ? "" : "s"} to the gallery`);
    } catch (e) { setNote(`✗ ${e.message || "Save failed"}`); }
    setSaving(false);
  }

  // Animated .mp4 = the whole shift (all pancakes); not affected by star/primary.
  // Two-step because a cold render takes minutes and the Hosting CDN cuts the
  // connection at ~120s: tap 1 fire-and-forgets the render (progress arrives on
  // the recap doc via onSnapshot), tap 2 — once the doc says ready — fetches the
  // cached bytes (fast) and opens the share sheet. The share MUST live in a tap
  // handler anyway (user-gesture requirement), so "ready → tap again" is free.
  async function videoButton() {
    setNote(null);
    if (!videoReady) {
      fetch(videoUrl).catch(() => {}); // kick the server render; the CDN 503 is expected noise
      setVidKicked(true);
      return;
    }
    setVidBusy(true);
    try {
      // Retry tap with the blob already in hand → share runs inside a fresh
      // gesture with no async gap, so the share sheet opens reliably.
      let blob = vidBlobRef.current;
      if (!blob) {
        const res = await fetch(videoUrl);
        if (!res.ok) throw new Error(`fetch ${res.status}`);
        blob = await res.blob();
        vidBlobRef.current = blob;
      }
      const how = await shareOrSave(blob, `recap-${shiftId}.mp4`, "video/mp4");
      if (how === "saved") setNote("⬇ Saved the video — tap again for the share sheet.");
    } catch (e) {
      // If the fetch died because the server decided to re-render (its build
      // version moved on), the doc flips to "rendering" — progress, not error.
      if (recapRef.current?.videoRenderStatus === "rendering") setNote("⏳ The server is re-rendering this video — progress below.");
      else setNote(`✗ Couldn’t fetch the video (${e.message || "network"}) — try again.`);
    }
    setVidBusy(false);
  }

  async function rebuild() {
    setBusy(true); setNote(null);
    try {
      const fn = httpsCallable(getFunctions(getApp()), "triggerShiftRecapBuild");
      const { data } = await fn({ shiftId });
      // the onSnapshot listener delivers the rebuilt doc
      setNote(`✓ Rebuilt (${data.pancakeCount} pancakes)`);
    } catch (e) { setNote(`✗ ${e.message || "Rebuild failed"}`); }
    setBusy(false);
  }

  return (
    <div style={{ maxWidth: 560, margin: "0 auto", padding: "1rem 1rem 2.5rem" }}>
      <div style={{ marginBottom: "0.9rem" }}>
        <div style={{ color: T.text, fontWeight: 700, fontSize: "1.05rem" }}>{recap.eventName || "Unnamed shift"}</div>
        <div style={{ color: T.dim, fontSize: "0.8rem" }}>{overlay.date} · {recap.pancakeCount} pancakes</div>
      </div>

      <div style={{ display: "flex", gap: "0.4rem", marginBottom: "0.8rem" }}>
        {["animated", "static"].map((t) => (
          <button key={t} onClick={() => setTab(t)} style={{
            flex: 1, padding: "0.5rem", borderRadius: 8, cursor: "pointer", fontFamily: FONT.body, fontWeight: 700, fontSize: "0.82rem",
            background: tab === t ? T.gold : T.card, color: tab === t ? T.bg : T.muted, border: `1px solid ${tab === t ? T.gold : T.border}`,
          }}>{t === "animated" ? "✨ Animated" : "🖼 Static"}</button>
        ))}
      </div>

      <div style={{ display: "flex", justifyContent: "center", marginBottom: "0.5rem", minHeight: 300 }}>
        {tab === "animated"
          ? <StoryAnimator width={320} pancakes={pancakes} overlay={overlay} />
          : <img key={staticUrl} src={staticUrl} alt="Static Story preview" style={{ width: 320, borderRadius: 14, boxShadow: "0 8px 30px rgba(0,0,0,0.5)", background: T.card, minHeight: 320 }} />}
      </div>
      {tab === "animated" && <div style={{ color: T.dim, fontSize: "0.72rem", textAlign: "center", marginBottom: "0.9rem" }}>Animated preview uses all {pancakes.length} pancakes. Stars below pick the {sel.size ? sel.size + " " : ""}static-post highlights.</div>}

      <button onClick={shareStatic} disabled={busy} style={{
        width: "100%", background: T.gold, color: T.bg, border: "none", borderRadius: 10, padding: "0.7rem", fontSize: "0.9rem",
        fontWeight: 700, fontFamily: FONT.body, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.6 : 1, marginBottom: "0.4rem",
      }}>{busy ? "Preparing…" : "📲 Share / Save static image"}</button>
      <button onClick={videoButton} disabled={vidBusy || vidKicked || videoRendering} style={{
        width: "100%", background: T.card, color: T.cream, border: `1px solid ${T.gold}`, borderRadius: 10,
        padding: "0.7rem", fontSize: "0.9rem", fontWeight: 700, fontFamily: FONT.body,
        cursor: (vidBusy || vidKicked || videoRendering) ? "wait" : "pointer", opacity: (vidBusy || vidKicked || videoRendering) ? 0.7 : 1, marginBottom: "0.35rem",
      }}>{vidBusy ? "Preparing share…"
        : vidKicked ? "⏳ Starting render…"
        : videoRendering ? `⏳ Rendering… ${videoPct}%`
        : videoReady ? "✨ Download / Share .mp4 (animated)"
        : videoError ? "↻ Retry video render"
        : "✨ Render .mp4 video (animated)"}</button>
      {videoRendering && (
        <div style={{ marginBottom: "0.35rem" }}>
          <div style={{ height: 6, borderRadius: 3, background: T.card, border: `1px solid ${T.border}`, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${Math.max(videoPct, 2)}%`, background: T.gold, borderRadius: 3, transition: "width 0.6s ease" }} />
          </div>
          <div style={{ color: T.dim, fontSize: "0.7rem", textAlign: "center", marginTop: "0.25rem" }}>
            {videoPct >= 97 ? "Encoding + saving…" : `Frame ${Math.round((videoPct / 100) * 450)} of 450`} · {Math.floor(videoElapsedS / 60)}:{String(videoElapsedS % 60).padStart(2, "0")} elapsed · usually ~4–6 min total
          </div>
        </div>
      )}
      <div style={{ color: T.dim, fontSize: "0.72rem", textAlign: "center", marginBottom: "1.25rem" }}>
        {videoReady
          ? <>15-sec 9:16 video of all {pancakes.length} pancakes — ready to share.</>
          : videoRendering
            ? <>Rendering happens on the server — safe to leave this page and come back.</>
            : <>15-sec 9:16 video of all {pancakes.length} pancakes. Renders on the server (~4–6 min) — safe to leave this page.</>}
        {videoError && <span style={{ color: "#ef537e" }}> · ✗ Last render failed{recap.videoRenderError ? `: ${recap.videoRenderError}` : ""}</span>}
        {note && <span style={{ color: note.startsWith("✗") ? "#ef537e" : T.green }}> · {note}</span>}
      </div>

      <div style={sectionLabel}>Spotlight pancakes &nbsp;<span style={{ color: T.dim, textTransform: "none", letterSpacing: 0 }}>({sel.size} starred)</span></div>
      <div style={{ color: T.dim, fontSize: "0.74rem", margin: "0 0 0.7rem 0.25rem", lineHeight: 1.5 }}>
        Tap to ⭐ include in the static post; tap 👑 to make one the big hero. Save applies stars as ⭐ showpieces in the public gallery.
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(92px, 1fr))", gap: "0.5rem" }}>
        {pancakes.map((p) => {
          const on = sel.has(p.orderId);
          const hero = heroId === p.orderId;
          return (
            <button key={p.orderId} onClick={() => toggle(p.orderId)} title={p.title} style={{
              position: "relative", aspectRatio: "1", borderRadius: 10, cursor: "pointer", padding: 0, background: T.card, overflow: "hidden",
              border: `${hero ? 3 : 2}px solid ${on ? T.gold : "transparent"}`, boxShadow: on ? `0 0 0 2px ${T.gold}55` : "none",
            }}>
              <img src={p.thumbUrl || p.cutoutUrl} alt={p.title} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "contain", opacity: on ? 1 : 0.5 }} />
              <span style={{ position: "absolute", top: 3, right: 5, fontSize: "0.95rem", filter: on ? "none" : "grayscale(1) opacity(0.5)" }}>{on ? "⭐" : "☆"}</span>
              <span onClick={(e) => { e.stopPropagation(); setPrimary(p.orderId); }} title="Make this the big hero"
                style={{ position: "absolute", top: 2, left: 4, fontSize: "0.95rem", cursor: "pointer", lineHeight: 1, filter: hero ? "none" : "grayscale(1) opacity(0.4)" }}>👑</span>
              {hero && <span style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: T.gold, color: T.bg, fontSize: "0.6rem", fontWeight: 700, textAlign: "center", letterSpacing: "0.05em" }}>HERO</span>}
            </button>
          );
        })}
      </div>

      <button onClick={saveSpotlights} disabled={!dirty || saving} style={{
        width: "100%", marginTop: "1rem", borderRadius: 10, padding: "0.7rem", fontSize: "0.88rem", fontWeight: 700, fontFamily: FONT.body,
        cursor: (!dirty || saving) ? "default" : "pointer",
        background: dirty ? T.pink : T.card, color: dirty ? T.text : T.dim,
        border: `1px solid ${dirty ? T.pink : T.border}`, opacity: saving ? 0.6 : 1,
      }}>{saving ? "Saving…" : dirty ? `💾 Save ${sel.size} spotlight${sel.size === 1 ? "" : "s"} to gallery` : "✓ Spotlights saved"}</button>

      <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem" }}>
        <a href={`${window.location.origin}/recap/${shiftId}`} target="_blank" rel="noreferrer" style={{ flex: 1, textAlign: "center", textDecoration: "none", background: T.card, color: T.pink, border: `1px solid ${T.border}`, borderRadius: 8, padding: "0.5rem", fontSize: "0.8rem", fontWeight: 700, fontFamily: FONT.body }}>🔗 Recap page</a>
        <button onClick={rebuild} disabled={busy} style={{ flex: 1, background: T.card, color: T.sky, border: `1px solid ${T.border}`, borderRadius: 8, padding: "0.5rem", fontSize: "0.8rem", fontWeight: 700, fontFamily: FONT.body, cursor: busy ? "wait" : "pointer" }}>🔄 Rebuild recap</button>
      </div>
    </div>
  );
}

const navBtn = { background: "transparent", border: `1px solid ${T.border}`, color: T.muted, borderRadius: 8, padding: "0.35rem 0.7rem", fontSize: "0.78rem", fontWeight: 600, cursor: "pointer", fontFamily: FONT.body };
const sectionLabel = { color: "rgba(255,218,173,0.55)", fontFamily: FONT.display, fontSize: "0.72rem", fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", margin: "0 0 0.6rem 0.25rem" };
const rowBtn = { display: "flex", alignItems: "center", gap: "0.75rem", background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, padding: "0.75rem 0.9rem", cursor: "pointer", textAlign: "left", width: "100%", fontFamily: FONT.body };
